import { Link, useLocation } from "@tanstack/react-router";
import { Briefcase, ChevronRight, SlidersHorizontal, Tag, Users } from "lucide-react";
import { cn } from "@/lib/utils";

const settingsTabs = [
  { label: "Activity Categories", href: "/dashboard/settings/categories", icon: Tag },
  { label: "Job Types", href: "/dashboard/settings/job-types", icon: Briefcase },
  { label: "Users", href: "/dashboard/settings/users", icon: Users },
  { label: "System", href: "/dashboard/settings/system", icon: SlidersHorizontal },
];

export function SettingsLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  return (
    <>
      {/* Topbar */}
      <div className="topbar">
        <div className="topbar__crumbs">
          <span>Admin</span>
          <ChevronRight size={14} />
          <strong>Settings</strong>
        </div>
      </div>

      {/* Page */}
      <div className="page">
        {/* Page header */}
        <div className="page-header">
          <div>
            <h1 className="page-header__title">Settings</h1>
            <p className="page-header__sub">Manage your workspace configuration.</p>
          </div>
        </div>

        {/* Tab navigation */}
        <div className="relative mb-8">
          <div className="overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            <nav className="flex gap-1 whitespace-nowrap">
              {settingsTabs.map((tab) => {
                const isActive = location.pathname === tab.href;

                return (
                  <Link
                    key={tab.href}
                    to={tab.href}
                    role="tab"
                    aria-selected={isActive}
                    className={cn(
                      "group relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors rounded-md",
                      isActive
                        ? "text-foreground"
                        : "text-muted-foreground hover:text-foreground/80",
                    )}
                  >
                    <tab.icon
                      className={cn(
                        "h-4 w-4 transition-colors",
                        isActive
                          ? "text-primary"
                          : "text-muted-foreground/60 group-hover:text-muted-foreground",
                      )}
                    />
                    <span>{tab.label}</span>

                    {/* Active indicator */}
                    {isActive && (
                      <span className="absolute bottom-0 left-3 right-3 h-0.5 rounded-full bg-primary" />
                    )}
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* Baseline border */}
          <div className="absolute bottom-0 left-0 right-0 h-px bg-border" />
        </div>

        {/* Tab content */}
        {children}
      </div>
    </>
  );
}
